import React, { useState, useEffect, useContext } from 'react';
import { API, AuthContext } from '../../App';
import axios from 'axios';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Card, CardContent, CardHeader } from '../ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Switch } from '../ui/switch';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem } from '../ui/command';
import { toast } from 'sonner';
import {
  Users, Plus, Edit, Trash2, Phone, Mail, Shield, UserCheck,
  Eye, EyeOff, ChevronsUpDown, Check, ChevronDown, ChevronRight, User
} from 'lucide-react';
import { COUNTRY_CODES } from '../../data/countryCodes';

/* ── constants ─────────────────────────────────────────────────────────────── */
const PERMISSION_LABELS = {
  view_exhibitions: "Fuarları Görüntüleme",
  create_exhibitions: "Fuar Oluşturma",
  edit_stands: "Stand Düzenleme",
  sell_stands: "Stand Satışı / Atama",
  manage_companies: "Firma Yönetimi",
  manage_contracts: "Sözleşme Yönetimi",
  view_reports: "Rapor Görüntüleme",
  manage_team: "Ekip Yönetimi",
};
const DEFAULT_PERMISSIONS = {
  view_exhibitions: true, create_exhibitions: false, edit_stands: true,
  sell_stands: true, manage_companies: false, manage_contracts: false,
  view_reports: false, manage_team: false,
};
const POSITIONS = ['Belirsiz', 'Genel Müdür', 'Proje Müdürü', 'Mali Müşavir', 'Satış Temsilcisi', 'Koordinatör', 'Asistan', 'Diğer'];
const DEPARTMENTS = ['Atanmadı', 'Muhasebe', 'Satış', 'Teknik', 'İnsan Kaynakları', 'Pazarlama', 'Operasyon'];
const AVATAR_COLORS = ['bg-amber-400', 'bg-teal-500', 'bg-blue-500', 'bg-purple-500', 'bg-pink-500', 'bg-orange-400', 'bg-emerald-500', 'bg-sky-500'];
const MAX_USERS = 1000;

const PASSWORD_REGEX = /^(?=.*[a-z])(?=.*[A-Z])(?=.*[%&?!@#$]).{6,}$/;
function validatePassword(pw) {
  if (!pw) return null;
  if (pw.length < 6) return 'En az 6 karakter olmalı';
  if (!/[a-z]/.test(pw)) return 'En az 1 küçük harf olmalı';
  if (!/[A-Z]/.test(pw)) return 'En az 1 büyük harf olmalı';
  if (!/[%&?!@#$]/.test(pw)) return 'En az 1 özel karakter (%&?!@#$) olmalı';
  return null;
}
function formatName(val) {
  return val.replace(/[^a-zA-ZçğıöşüÇĞİÖŞÜ\s]/g, '')
    .split(' ').map(w => w.length ? w.charAt(0).toUpperCase() + w.slice(1).toLowerCase() : '').join(' ');
}
function getEmptyForm() {
  return { name: '', email: '', password: '', passwordConfirm: '', phone_country_code: '+90', phone_number: '', position: 'Belirsiz', department: 'Atanmadı', permissions: { ...DEFAULT_PERMISSIONS } };
}
function avatarColor(name) {
  let h = 0;
  for (let i = 0; i < (name || '').length; i++) h += name.charCodeAt(i);
  return AVATAR_COLORS[h % AVATAR_COLORS.length];
}

/* ── PhoneCodeSelect ────────────────────────────────────────────────────────── */
function PhoneCodeSelect({ value, onChange }) {
  const [open, setOpen] = useState(false);
  const sel = COUNTRY_CODES.find(c => c.code === value) || { flag: '🌍', code: value };
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" className="w-36 justify-between font-normal h-10 text-sm shrink-0 px-2">
          <span>{sel.flag} {sel.code}</span><ChevronsUpDown className="h-3 w-3 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-64 p-0" align="start">
        <Command><CommandInput placeholder="Ülke ara..." className="h-9" />
          <CommandList><CommandEmpty>Bulunamadı</CommandEmpty>
            <CommandGroup>
              {COUNTRY_CODES.map(c => (
                <CommandItem key={c.code+c.name} value={`${c.name} ${c.code}`}
                  onSelect={() => { onChange(c.code); setOpen(false); }} className="text-sm">
                  <span className="mr-2">{c.flag}</span>{c.name}<span className="text-gray-400 ml-1">{c.code}</span>
                  {value === c.code && <Check className="ml-auto h-3.5 w-3.5" />}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

/* ── InlineBadgeSelect ──────────────────────────────────────────────────────── */
function InlineBadgeSelect({ label, value, options, onChange, testId }) {
  const [open, setOpen] = useState(false);
  const isEmpty = !value || value === 'Belirsiz' || value === 'Atanmadı';
  return (
    <div className="space-y-0.5">
      <p className="text-[10px] text-gray-400 leading-none">{label}</p>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            className={`text-xs px-2 py-0.5 rounded-full border flex items-center gap-0.5 transition-colors ${isEmpty ? 'text-blue-600 border-blue-200 bg-blue-50 hover:bg-blue-100' : 'text-teal-700 border-teal-200 bg-teal-50 hover:bg-teal-100'}`}
            data-testid={testId}
          >
            {value || options[0]}
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-44 p-1" align="start">
          <div className="space-y-0.5">
            {options.map(o => (
              <button key={o} className={`w-full text-left text-xs px-2 py-1.5 rounded hover:bg-gray-100 flex items-center justify-between ${value === o ? 'font-semibold text-teal-700' : 'text-gray-700'}`}
                onClick={() => { onChange(o); setOpen(false); }}>
                {o}{value === o && <Check className="w-3 h-3 text-teal-600" />}
              </button>
            ))}
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}

/* ── Collapsible Section ────────────────────────────────────────────────────── */
function CollapsibleSection({ title, subtitle, count, badgeColor, emptyIcon, emptyTitle, emptyText, children }) {
  const [open, setOpen] = useState(false);
  return (
    <Card className="border border-gray-200 shadow-sm overflow-hidden">
      <button onClick={() => setOpen(o => !o)} className="w-full text-left">
        <div className="px-5 py-4 flex items-center justify-between">
          <div>
            <p className="text-sm font-semibold text-gray-800">{title}</p>
            <p className="text-xs text-gray-500 mt-0.5">{subtitle}</p>
          </div>
          <div className="flex items-center gap-2">
            <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${badgeColor}`}>{count}</span>
            {open ? <ChevronDown className="w-4 h-4 text-gray-400" /> : <ChevronRight className="w-4 h-4 text-gray-400" />}
          </div>
        </div>
      </button>
      {open && (
        <div className="border-t border-gray-100">
          {count === 0 || !children ? (
            <div className="py-10 text-center">
              <div className="w-12 h-12 mx-auto rounded-full bg-gray-100 flex items-center justify-center mb-3">
                <User className="w-6 h-6 text-gray-400" />
              </div>
              <p className="text-sm font-medium text-gray-500">{emptyTitle}</p>
              <p className="text-xs text-gray-400 mt-1">{emptyText}</p>
            </div>
          ) : children}
        </div>
      )}
    </Card>
  );
}

/* ── Main Component ─────────────────────────────────────────────────────────── */
export default function OrganizerTeamMembers() {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingMember, setEditingMember] = useState(null);
  const [previewMember, setPreviewMember] = useState(null);
  const [form, setForm] = useState(getEmptyForm());
  const [showPw1, setShowPw1] = useState(false);
  const [showPw2, setShowPw2] = useState(false);
  const [errors, setErrors] = useState({});

  const { user: currentUser } = useContext(AuthContext);
  const token = () => localStorage.getItem('token');

  useEffect(() => { fetchMembers(); }, []);

  const fetchMembers = async () => {
    try {
      const res = await axios.get(`${API}/team-members`, { headers: { Authorization: `Bearer ${token()}` } });
      setMembers(res.data);
    } catch { toast.error('Kullanıcılar yüklenemedi'); }
    finally { setLoading(false); }
  };

  const openCreate = () => { setEditingMember(null); setForm(getEmptyForm()); setErrors({}); setShowDialog(true); };
  const openEdit = (m) => {
    setEditingMember(m);
    setForm({ name: m.name||'', email: m.email||'', password: '', passwordConfirm: '',
      phone_country_code: m.phone_country_code||'+90', phone_number: m.phone_number||'',
      position: m.position||'Belirsiz', department: m.department||'Atanmadı',
      permissions: { ...DEFAULT_PERMISSIONS, ...m.permissions } });
    setErrors({}); setShowDialog(true);
  };

  const isFormValid = () => {
    if (!form.name || !form.email) return false;
    // Şifre sağlandıysa doğrula; yeni üyede boş bırakılabilir (davet emaili gider)
    if (form.password) {
      if (validatePassword(form.password)) return false;
      if (form.password !== form.passwordConfirm) return false;
    }
    return true;
  };

  const handleSave = async () => {
    if (!isFormValid()) { toast.error('Lütfen tüm alanları doğru doldurun'); return; }
    try {
      const payload = { name: form.name, email: form.email, phone_country_code: form.phone_country_code,
        phone_number: form.phone_number, position: form.position, department: form.department,
        permissions: form.permissions };
      if (form.password) payload.password = form.password;
      if (editingMember) {
        await axios.patch(`${API}/team-members/${editingMember.id}`, payload, { headers: { Authorization: `Bearer ${token()}` } });
        toast.success('Kullanıcı güncellendi');
      } else {
        await axios.post(`${API}/team-members`, payload, { headers: { Authorization: `Bearer ${token()}` } });
        toast.success(form.password
          ? `Kullanıcı oluşturuldu! Davet emaili ${form.email} adresine gönderildi.`
          : `Davet emaili ${form.email} adresine gönderildi. Kullanıcı kendi şifresini oluşturacak.`
        );
      }
      setShowDialog(false); fetchMembers();
    } catch (err) { toast.error(err.response?.data?.detail || 'İşlem başarısız'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Silmek istediğinize emin misiniz?')) return;
    try {
      await axios.delete(`${API}/team-members/${id}`, { headers: { Authorization: `Bearer ${token()}` } });
      toast.success('Kullanıcı silindi'); fetchMembers();
    } catch { toast.error('Silinemedi'); }
  };

  const toggleActive = async (m) => {
    try {
      await axios.patch(`${API}/team-members/${m.id}`, { is_active: !m.is_active }, { headers: { Authorization: `Bearer ${token()}` } });
      toast.success(m.is_active ? 'Kullanıcı pasife alındı' : 'Kullanıcı aktifleştirildi');
      fetchMembers();
    } catch { toast.error('İşlem başarısız'); }
  };

  const updateMemberField = async (m, key, val) => {
    try {
      await axios.patch(`${API}/team-members/${m.id}`, { [key]: val }, { headers: { Authorization: `Bearer ${token()}` } });
      setMembers(prev => prev.map(mb => mb.id === m.id ? { ...mb, [key]: val } : mb));
    } catch { toast.error('Güncellenemedi'); }
  };

  const activeMembers = members.filter(m => m.is_active);
  const passiveMembers = members.filter(m => !m.is_active);

  return (
    <div className="space-y-4" data-testid="team-members-page">

      {/* ── Header ─────────────────────────────────────────────────────── */}
      <div className="flex items-start justify-between">
        <div>
          <h2 className="text-xl font-bold text-gray-900">Kullanıcı Yönetimi</h2>
          <p className="text-sm text-gray-500 mt-0.5">Takım üyelerini davet edin ve kullanıcı yetkilerini yönetin</p>
        </div>
        <div className="bg-teal-50 border border-teal-200 rounded-xl px-4 py-2 text-right shrink-0">
          <p className="text-xs text-teal-600 font-semibold">Organizatör</p>
          <p className="text-lg font-bold text-teal-700">{members.length + 1} / {MAX_USERS}</p>
          <p className="text-xs text-teal-500">{MAX_USERS - members.length - 1} slot kaldı</p>
        </div>
      </div>

      {/* ── Action card ────────────────────────────────────────────────── */}
      <Card className="border border-gray-200 shadow-sm">
        <CardContent className="px-5 py-4 flex items-center justify-between">
          <div>
            <p className="text-sm font-semibold text-gray-800">Kullanıcı Yönetimi</p>
            <p className="text-xs text-gray-500">Takımınıza yeni üye ekleyin veya davet gönderin</p>
          </div>
          <div className="flex items-center gap-2">
            <Button onClick={openCreate} className="bg-blue-600 hover:bg-blue-700 gap-1.5 text-xs h-8 px-3"
              data-testid="add-member-btn">
              <Plus className="w-3.5 h-3.5" /> Kullanıcı Ekle
            </Button>
            <Button className="bg-teal-600 hover:bg-teal-700 gap-1.5 text-xs h-8 px-3">
              <Plus className="w-3.5 h-3.5" /> Davet Gönder
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* ── Team Members ───────────────────────────────────────────────── */}
      <Card className="border border-gray-200 shadow-sm overflow-hidden">
        <CardHeader className="px-5 py-3 border-b border-gray-100">
          <p className="text-sm font-semibold text-gray-800">Takım Üyeleri</p>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="py-10 text-center text-sm text-gray-400">Yükleniyor...</div>
          ) : (
            <div className="divide-y divide-gray-100">
              {/* Organizer row (self - always at top) */}
              {currentUser && (
                <div className="px-5 py-3.5 flex items-center gap-4 bg-amber-50/40">
                  <div className="relative shrink-0">
                    <div className="w-10 h-10 rounded-full bg-amber-400 flex items-center justify-center">
                      <User className="w-5 h-5 text-white" />
                    </div>
                    <span className="absolute -top-1 -right-1 bg-amber-400 text-white text-[9px] px-1 py-0.5 rounded-full font-bold whitespace-nowrap">
                      Tam Yetki
                    </span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-gray-900">{currentUser.name || 'Yönetici'}</p>
                    <p className="text-xs text-gray-500">{currentUser.email || ''}</p>
                  </div>
                  <div className="hidden md:flex items-center gap-6 shrink-0">
                    <div className="space-y-0.5">
                      <p className="text-[10px] text-gray-400">Pozisyon</p>
                      <span className="text-xs px-2 py-0.5 rounded-full bg-blue-50 text-blue-600 border border-blue-100">Organizatör</span>
                    </div>
                    <div className="space-y-0.5">
                      <p className="text-[10px] text-gray-400">Departman</p>
                      <span className="text-xs px-2 py-0.5 rounded-full bg-gray-50 text-gray-500 border border-gray-200">—</span>
                    </div>
                  </div>
                  <div className="shrink-0">
                    <span className="inline-flex items-center px-3 py-1 rounded-md bg-gray-800 text-white text-xs font-semibold">
                      AKTİF
                    </span>
                  </div>
                </div>
              )}

              {/* Member rows */}
              {activeMembers.map((m, idx) => (
                <div key={m.id} className={`px-5 py-3.5 flex items-center gap-4 ${idx % 2 === 1 ? 'bg-gray-50/50' : ''}`}
                  data-testid={`member-row-${m.id}`}>
                  <div className={`w-10 h-10 rounded-full ${avatarColor(m.name)} flex items-center justify-center shrink-0`}>
                    <User className="w-5 h-5 text-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-gray-900">{m.name}</p>
                    <p className="text-xs text-gray-500">{m.email}</p>
                  </div>
                  <div className="hidden md:flex items-center gap-6 shrink-0">
                    <InlineBadgeSelect
                      label="Pozisyon"
                      value={m.position || 'Belirsiz'}
                      options={POSITIONS}
                      onChange={v => updateMemberField(m, 'position', v)}
                      testId={`member-position-${m.id}`}
                    />
                    <InlineBadgeSelect
                      label="Departman"
                      value={m.department || 'Atanmadı'}
                      options={DEPARTMENTS}
                      onChange={v => updateMemberField(m, 'department', v)}
                      testId={`member-department-${m.id}`}
                    />
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <button
                      onClick={() => setPreviewMember(m)}
                      className="p-1.5 rounded-md text-blue-400 hover:text-blue-600 hover:bg-blue-50 transition-colors"
                      title="Önizle"
                      data-testid={`preview-member-${m.id}`}>
                      <Eye className="w-3.5 h-3.5" />
                    </button>
                    <button
                      onClick={() => toggleActive(m)}
                      className="inline-flex items-center px-3 py-1 rounded-md bg-gray-800 hover:bg-gray-700 text-white text-xs font-semibold transition-colors"
                      data-testid={`toggle-active-${m.id}`}
                    >
                      AKTİF
                    </button>
                    <button onClick={() => openEdit(m)}
                      className="p-1.5 rounded-md text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-colors"
                      data-testid={`edit-member-${m.id}`}>
                      <Edit className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              ))}

              {activeMembers.length === 0 && (
                <div className="py-10 text-center">
                  <Users className="w-10 h-10 mx-auto text-gray-300 mb-2" />
                  <p className="text-sm text-gray-500">Henüz ekip üyesi eklenmemiş</p>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* ── Collapsible sections ────────────────────────────────────────── */}
      <CollapsibleSection
        title="Davet Bekleyen Kullanıcılar"
        subtitle="Henüz kaydını tamamlamamış kullanıcılar"
        count={0}
        badgeColor="bg-orange-100 text-orange-600"
        emptyTitle="Bekleyen davet bulunmuyor"
        emptyText="Yeni kullanıcılar davet ettiğinizde burada görünecek"
      />

      <CollapsibleSection
        title="Pasif Kullanıcılar"
        subtitle="Hesabı pasif hale getirilmiş kullanıcılar"
        count={passiveMembers.length}
        badgeColor="bg-red-100 text-red-600"
        emptyTitle="Pasif kullanıcı bulunmuyor"
        emptyText="Tüm kullanıcılar aktif durumda"
      >
        {passiveMembers.length > 0 && (
          <div className="divide-y divide-gray-100">
            {passiveMembers.map((m) => (
              <div key={m.id} className="px-5 py-3.5 flex items-center gap-4 opacity-60"
                data-testid={`passive-member-${m.id}`}>
                <div className={`w-10 h-10 rounded-full ${avatarColor(m.name)} flex items-center justify-center shrink-0`}>
                  <User className="w-5 h-5 text-white" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-gray-900">{m.name}</p>
                  <p className="text-xs text-gray-500">{m.email}</p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <button onClick={() => toggleActive(m)}
                    className="inline-flex items-center px-3 py-1 rounded-md bg-gray-400 hover:bg-gray-500 text-white text-xs font-semibold transition-colors">
                    AKSİF
                  </button>
                  <button onClick={() => handleDelete(m.id)}
                    className="p-1.5 rounded-md text-red-400 hover:text-red-600 hover:bg-red-50 transition-colors"
                    data-testid={`delete-member-${m.id}`}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </CollapsibleSection>

      <CollapsibleSection
        title="Arşiv Kullanıcılar"
        subtitle="İşten ayrılmış kullanıcılar (sadece kayıt amaçlı)"
        count={0}
        badgeColor="bg-purple-100 text-purple-600"
        emptyTitle="Arşivlenmiş kullanıcı bulunmuyor"
        emptyText="Pasif kullanıcıları arşivleyerek burada görebilirsiniz"
      />

      {/* ── Yakında Geliyor ─────────────────────────────────────────────── */}
      <Card className="border border-blue-100 bg-blue-50/50 shadow-sm">
        <CardContent className="px-5 py-4">
          <p className="text-sm font-semibold text-blue-800 mb-2">Yakında Geliyor</p>
          <ul className="space-y-1.5">
            {['Toplu kullanıcı işlemleri', 'Kullanıcı detay görüntüleme', 'Kullanıcı aktivite logları'].map(f => (
              <li key={f} className="flex items-center gap-2 text-xs text-blue-700">
                <span className="w-1 h-1 rounded-full bg-blue-400 shrink-0" /> {f}
              </li>
            ))}
          </ul>
        </CardContent>
      </Card>

      {/* ── Preview Modal ────────────────────────────────────────────── */}
      {previewMember && (
        <Dialog open={!!previewMember} onOpenChange={() => setPreviewMember(null)}>
          <DialogContent className="max-w-sm" data-testid="member-preview-modal">
            <DialogHeader>
              <DialogTitle>Kullanıcı Detayı</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 pt-2">
              {/* Avatar + isim */}
              <div className="flex items-center gap-3">
                <div className={`w-14 h-14 rounded-full ${avatarColor(previewMember.name)} flex items-center justify-center`}>
                  <User className="w-7 h-7 text-white" />
                </div>
                <div>
                  <p className="font-semibold text-gray-900">{previewMember.name}</p>
                  <p className="text-xs text-gray-500">{previewMember.email}</p>
                  <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold ${previewMember.is_active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-600'}`}>
                    {previewMember.is_active ? 'Aktif' : 'Pasif'}
                  </span>
                </div>
              </div>
              {/* Bilgiler */}
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div className="space-y-0.5">
                  <p className="text-xs text-gray-400">Pozisyon</p>
                  <p className="font-medium text-gray-800">{previewMember.position || 'Belirsiz'}</p>
                </div>
                <div className="space-y-0.5">
                  <p className="text-xs text-gray-400">Departman</p>
                  <p className="font-medium text-gray-800">{previewMember.department || 'Atanmadı'}</p>
                </div>
                <div className="space-y-0.5">
                  <p className="text-xs text-gray-400">Telefon</p>
                  <p className="font-medium text-gray-800">
                    {previewMember.phone_number ? `${previewMember.phone_country_code} ${previewMember.phone_number}` : '—'}
                  </p>
                </div>
                <div className="space-y-0.5">
                  <p className="text-xs text-gray-400">Katılım</p>
                  <p className="font-medium text-gray-800">
                    {previewMember.created_at ? new Date(previewMember.created_at).toLocaleDateString('tr-TR') : '—'}
                  </p>
                </div>
              </div>
              {/* Yetkiler */}
              <div>
                <p className="text-xs text-gray-400 mb-2 flex items-center gap-1">
                  <Shield className="w-3 h-3" /> Yetkiler
                </p>
                <div className="flex flex-wrap gap-1.5">
                  {Object.entries(PERMISSION_LABELS).map(([key, label]) => (
                    previewMember.permissions?.[key] ? (
                      <span key={key} className="text-[10px] px-2 py-0.5 bg-teal-50 text-teal-700 border border-teal-100 rounded-full">
                        {label}
                      </span>
                    ) : null
                  ))}
                  {!Object.values(previewMember.permissions || {}).some(Boolean) && (
                    <span className="text-xs text-gray-400">Yetki atanmamış</span>
                  )}
                </div>
              </div>
              <div className="flex gap-2 pt-1">
                <Button variant="outline" onClick={() => setPreviewMember(null)} className="flex-1">Kapat</Button>
                <Button onClick={() => { openEdit(previewMember); setPreviewMember(null); }} className="flex-1 bg-blue-600 hover:bg-blue-700">
                  <Edit className="w-3.5 h-3.5 mr-1.5" /> Düzenle
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* ── Create/Edit Dialog ──────────────────────────────────────────── */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="member-dialog">
          <DialogHeader>
            <DialogTitle>{editingMember ? 'Kullanıcı Düzenle' : 'Yeni Kullanıcı Ekle'}</DialogTitle>
            <DialogDescription>
              {editingMember ? 'Kullanıcı bilgilerini ve yetkilerini güncelleyin' : 'Yeni bir ekip üyesi oluşturun'}
            </DialogDescription>
          </DialogHeader>
          <form autoComplete="off" onSubmit={e => { e.preventDefault(); handleSave(); }}>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5 col-span-2">
                  <Label className="text-xs">Ad Soyad *</Label>
                  <Input value={form.name} onChange={e => setForm(p => ({ ...p, name: formatName(e.target.value) }))}
                    placeholder="Ad Soyad" autoComplete="off" data-testid="member-name-input" />
                </div>
                <div className="space-y-1.5 col-span-2">
                  <Label className="text-xs">E-posta *</Label>
                  <Input type="email" value={form.email} onChange={e => setForm(p => ({ ...p, email: e.target.value }))}
                    placeholder="kullanici@firma.com" autoComplete="off" data-testid="member-email-input" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Pozisyon</Label>
                  <select value={form.position} onChange={e => setForm(p => ({ ...p, position: e.target.value }))}
                    className="w-full h-9 border border-gray-200 rounded-md px-3 text-sm bg-white">
                    {POSITIONS.map(p => <option key={p} value={p}>{p}</option>)}
                  </select>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Departman</Label>
                  <select value={form.department} onChange={e => setForm(p => ({ ...p, department: e.target.value }))}
                    className="w-full h-9 border border-gray-200 rounded-md px-3 text-sm bg-white">
                    {DEPARTMENTS.map(d => <option key={d} value={d}>{d}</option>)}
                  </select>
                </div>
              </div>

              <div className="space-y-1.5">
                <Label className="text-xs">{editingMember ? 'Yeni Şifre (boş = değişmez)' : 'Şifre (opsiyonel)'}</Label>
                <div className="relative">
                  <Input type={showPw1 ? 'text' : 'password'} value={form.password}
                    onChange={e => setForm(p => ({ ...p, password: e.target.value }))}
                    onBlur={() => { if (form.password) { const e = validatePassword(form.password); setErrors(p => ({ ...p, password: e })); } }}
                    placeholder={editingMember ? 'Şifre değiştirmek için doldurun' : 'Boş bırakılırsa davet e-postası gönderilir'}
                    autoComplete="new-password"
                    className={`pr-10 ${errors.password ? 'border-red-400' : ''}`} data-testid="member-password-input" />
                  <button type="button" onClick={() => setShowPw1(v => !v)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400" tabIndex={-1}>
                    {showPw1 ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                {errors.password && <p className="text-xs text-red-500">{errors.password}</p>}
                {!editingMember && !form.password && (
                  <p className="text-[10px] text-teal-600 bg-teal-50 px-2 py-1 rounded">
                    Boş bırakılırsa kullanıcıya davet e-postası gönderilir, kendi şifresini oluşturur.
                  </p>
                )}
                <p className="text-[10px] text-gray-400">En az 6 karakter, 1 büyük, 1 küçük, 1 özel karakter (%&?!@#$)</p>
              </div>

              <div className="space-y-1.5">
                <Label className="text-xs">{editingMember ? 'Yeni Şifre Tekrar' : 'Şifre Tekrar *'}</Label>
                <div className="relative">
                  <Input type={showPw2 ? 'text' : 'password'} value={form.passwordConfirm}
                    onChange={e => setForm(p => ({ ...p, passwordConfirm: e.target.value }))}
                    onBlur={() => { if (form.password && form.passwordConfirm !== form.password) setErrors(p => ({ ...p, passwordConfirm: 'Şifreler uyuşmuyor' })); else setErrors(p => ({ ...p, passwordConfirm: null })); }}
                    placeholder="Şifreyi tekrar girin" autoComplete="new-password"
                    className={`pr-10 ${errors.passwordConfirm ? 'border-red-400' : ''}`} data-testid="member-password-confirm-input" />
                  <button type="button" onClick={() => setShowPw2(v => !v)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400" tabIndex={-1}>
                    {showPw2 ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                {errors.passwordConfirm && <p className="text-xs text-red-500">{errors.passwordConfirm}</p>}
              </div>

              <div className="space-y-1.5">
                <Label className="text-xs">Telefon</Label>
                <div className="flex gap-2">
                  <PhoneCodeSelect value={form.phone_country_code} onChange={v => setForm(p => ({ ...p, phone_country_code: v }))} />
                  <Input type="tel" value={form.phone_number}
                    onChange={e => setForm(p => ({ ...p, phone_number: e.target.value.replace(/\D/g, '') }))}
                    placeholder="5551234567" className="flex-1" autoComplete="off" data-testid="phone-number-input" />
                </div>
              </div>

              <div className="space-y-2 pt-2 border-t">
                <Label className="text-xs flex items-center gap-1.5"><Shield className="w-3.5 h-3.5 text-teal-600" /> Yetkiler</Label>
                {Object.entries(PERMISSION_LABELS).map(([key, label]) => (
                  <div key={key} className="flex items-center justify-between py-0.5">
                    <span className="text-sm text-gray-700">{label}</span>
                    <Switch checked={form.permissions[key] || false}
                      onCheckedChange={v => setForm(p => ({ ...p, permissions: { ...p.permissions, [key]: v } }))}
                      data-testid={`perm-${key}`} />
                  </div>
                ))}
              </div>

              <div className="flex gap-2 pt-2">
                <Button type="button" variant="outline" onClick={() => setShowDialog(false)} className="flex-1">Vazgeç</Button>
                <Button type="submit" className="flex-1 bg-blue-600 hover:bg-blue-700" disabled={!isFormValid()} data-testid="save-member-btn">
                  {editingMember ? 'Güncelle' : 'Oluştur'}
                </Button>
              </div>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
